import React, { useEffect, useState } from "react";
import { campaignsApi, Campaign, Insights } from "../../../api/maintenance/campaigns";
import { money, num, ts, tierPays, tierBand, statusPill } from "./campaignUtils";
import { CampaignTier } from "../../../api/maintenance/campaigns";
import { useNotice } from "../shared/useNotice";

const BAND_LABEL: Record<string, string> = {
  under_500: "under ₹500", "500_749": "₹500 – 749", "750_999": "₹750 – 999", "1000_1499": "₹1,000 – 1,499",
  "1500_2999": "₹1,500 – 2,999", "3000_10000": "₹3,000 – 10,000", over_10000: "over ₹10,000",
};
const AUD_LABEL: Record<string, { name: string; why: string }> = {
  benefited: { name: "Earned cashback before", why: "The repeat engine. Only a fraction come back within 30 days." },
  holding_promo: { name: "Holding unspent cashback", why: "Money already promised and sitting idle." },
  near_miss: { name: "Near miss", why: "Topped up just under the floor. One nudge from qualifying." },
  dormant: { name: "Dormant", why: "Topped up before, nothing in 60 days. Win-back." },
  all: { name: "Everyone", why: "Every registered user." },
};

interface Props {
  campaigns: Campaign[];
  onOpen: (c: Campaign) => void;
  onNew: () => void;
  canManage: boolean;
}

const OverviewTab: React.FC<Props> = ({ campaigns, onOpen, onNew, canManage }) => {
  const [ins, setIns] = useState<Insights | null>(null);
  const [err, setErr] = useNotice("error", "");
  const [tiersByCampaign, setTiers] = useState<Record<number, CampaignTier[]>>({});

  useEffect(() => {
    campaignsApi.insights().then((r) => setIns(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Failed to load insights"));
  }, []);

  const live = campaigns.find((c) => c.status === "live" && c.kind === "offer") || null;
  const next = campaigns.filter((c) => c.status === "scheduled").sort((a, b) => a.starts_at.localeCompare(b.starts_at))[0] || null;

  useEffect(() => {
    [live, next].filter(Boolean).forEach((c) => {
      const cc = c as Campaign;
      if (tiersByCampaign[cc.id]) return;
      campaignsApi.get(cc.id).then((r) => setTiers((m) => ({ ...m, [cc.id]: r.data.tiers })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.id, next?.id]);

  const t = ins?.totals;
  const monthly = ins?.monthly || [];
  const thisMonth = monthly[monthly.length - 1];
  const floor = ins?.floor ?? 750;
  const bands = ins?.bands || [];
  const bandMax = Math.max(1, ...bands.map((b) => b.topups));
  const totalTopups = bands.reduce((a, b) => a + b.topups, 0);
  const earning = bands.filter((b) => b.lo >= floor).reduce((a, b) => a + b.topups, 0);

  const hero = (c: Campaign | null, label: string, pill: string, empty: string) => (
    <div className="ma-card half" style={{ padding: 16 }}>
      <div><span className={`ma-pill ${pill}`}>{label}</span></div>
      {c ? (
        <>
          <div style={{ fontSize: 18, fontWeight: 700, margin: "8px 0 2px" }}>{c.name}</div>
          <div className="sub" style={{ fontSize: 12, color: "#8a92a1" }}>
            {c.status === "scheduled" ? `Starts ${ts(c.starts_at)}` : `Since ${ts(c.starts_at)}`} · ends {ts(c.ends_at)} · {c.visibility}
            {c.audience !== "all" ? ` · ${c.audience}` : ""}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
            {(tiersByCampaign[c.id] || []).filter((x) => x.is_active).map((x) => (
              <span key={x.id} className="ma-pill muted">{tierBand(x)} <b>{tierPays(x)}</b></span>
            ))}
          </div>
          <p className="ma-hint" style={{ marginTop: 8 }}>
            {c.status === "live" ? <>Paid <b>{money(c.awarded_amount)}</b> across {num(c.awarded_count)} rewards{c.budget_cap ? ` · budget ${money(c.budget_cap)}` : ""}{c.max_awards ? ` · ${num(c.max_awards)} rewards max` : ""}.</>
              : c.forecast ? <>Forecast <b>{money(c.forecast.window.payout)}</b>{c.budget_cap ? ` against a ${money(c.budget_cap)} budget` : ""}.</> : null}
          </p>
          <button className="ma-btn" style={{ marginTop: 6 }} onClick={() => onOpen(c)}>Open</button>
        </>
      ) : (
        <>
          <div style={{ fontSize: 15, fontWeight: 600, margin: "8px 0 2px", color: "#8a92a1" }}>{empty}</div>
          {label === "Scheduled" && canManage && <button className="ma-btn primary" style={{ marginTop: 8 }} onClick={onNew}>+ New campaign</button>}
        </>
      )}
    </div>
  );

  return (
    <>
      {err && <div className="ma-error">{err}</div>}
      <div className="ma-grid" style={{ marginBottom: 16 }}>
        {hero(live, "Live now", "ok", ins?.program?.is_active ? "Standing rate card is paying" : "Program paused — nothing pays")}
        {hero(next, "Scheduled", "warn", "Nothing scheduled")}
      </div>

      <div className="ma-kpis">
        <div className="ma-kpi"><div className="ma-kpi-label">Paid this month</div><div className="ma-kpi-value">{money(t?.paid_this_month)}</div>
          <div className="ma-kpi-sub">{num(t?.paid_this_month_count)} cashbacks{thisMonth && thisMonth.gross ? ` · ${(thisMonth.cashback / thisMonth.gross * 100).toFixed(1)}% of gross` : ""}</div></div>
        <div className="ma-kpi"><div className="ma-kpi-label">Paid all time</div><div className="ma-kpi-value">{money(t?.paid_all_time)}</div><div className="ma-kpi-sub">{num(t?.paid_count)} settled</div></div>
        <div className="ma-kpi"><div className="ma-kpi-label">Unspent in wallets</div><div className="ma-kpi-value">{money(t?.unspent_promo)}</div>
          <div className="ma-kpi-sub">{num(t?.wallets_with_promo)} wallets{t?.paid_all_time ? ` · ${Math.round((t.unspent_promo / t.paid_all_time) * 100)}% of all granted` : ""}</div></div>
        <div className="ma-kpi"><div className="ma-kpi-label">Reachable by push</div><div className="ma-kpi-value">{num(ins?.audiences?.all?.reachable)}</div>
          <div className="ma-kpi-sub">of {num(ins?.audiences?.all?.users)} users</div></div>
        <div className="ma-kpi"><div className="ma-kpi-label">Came back in 30 days</div><div className="ma-kpi-value">{num(ins?.repeat?.repeated_30d)}</div>
          <div className="ma-kpi-sub">of {num(ins?.repeat?.earners)} cashback earners</div></div>
      </div>

      <div className="ma-grid">
        <div className="ma-card half">
          <div className="ma-card-h"><h3>Where the top-up money is</h3><span className="ma-hint">{num(totalTopups)} successful top-ups · last 180 days</span></div>
          <div className="ma-card-b">
            <div className="ma-legend" style={{ marginBottom: 10 }}>
              <span className="ma-lg"><i style={{ background: "#1565c0" }} /> Earns cashback</span>
              <span className="ma-lg"><i style={{ background: "#e65100" }} /> Earns nothing</span>
            </div>
            <div className="ma-bars">
              {bands.map((b) => (
                <div className="ma-bar" key={b.band} title={`${b.users} users · ${money(b.gross)}`}>
                  <span className="nm">{BAND_LABEL[b.band] || b.band}</span>
                  <span className="track"><span className="fill" style={{ width: `${(b.topups / bandMax) * 100}%`, background: b.lo >= floor ? "#1565c0" : "#e65100" }} /></span>
                  <span className="val">{num(b.topups)}</span>
                </div>
              ))}
            </div>
            {totalTopups > 0 && (
              <p className="ma-hint" style={{ marginTop: 10 }}>
                <b>{Math.round(((totalTopups - earning) / totalTopups) * 100)}% of top-ups earn nothing</b> under the ₹{num(floor)} floor.
              </p>
            )}
          </div>
        </div>

        <div className="ma-card half">
          <div className="ma-card-h"><h3>People hug the thresholds</h3><span className="ma-hint">exact amounts · last 180 days</span></div>
          <div className="ma-tablewrap"><table className="ma-table">
            <thead><tr><th>Amount</th><th className="num">Times</th><th>Earns</th></tr></thead>
            <tbody>
              {(ins?.exact_amounts || []).slice(0, 8).map((r) => (
                <tr key={r.amount}><td>{money(r.amount)}</td><td className="num">{num(r.n)}</td>
                  <td>{r.amount >= floor ? <span className="ma-pill ok">yes</span> : <span className="ma-pill muted">no</span>}</td></tr>
              ))}
            </tbody>
          </table></div>
        </div>

        <div className="ma-card">
          <div className="ma-card-h"><h3>Who you can talk to</h3><span className="ma-hint">computed live</span></div>
          <div className="ma-card-b" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 10 }}>
            {["benefited", "holding_promo", "near_miss", "dormant"].map((k) => {
              const a = ins?.audiences?.[k];
              return (
                <div key={k} style={{ border: "1px solid #e6e8ee", borderRadius: 10, padding: "12px 14px", display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{AUD_LABEL[k].name}</div>
                    <div className="ma-hint">{AUD_LABEL[k].why}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>{num(a?.users)}</div>
                    <div className="ma-hint">{num(a?.reachable)} reachable</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="ma-card-b" style={{ paddingTop: 0 }}>
            <div className="ma-note" style={{ marginBottom: 0 }}>Push and the in-app bell only. All registered SMS templates are transactional; a promotional SMS needs a new DLT registration.</div>
          </div>
        </div>

        <div className="ma-card">
          <div className="ma-card-h"><h3>What cashback has cost, by month</h3></div>
          <div className="ma-tablewrap"><table className="ma-table">
            <thead><tr><th>Month</th><th className="num">Top-ups</th><th className="num">Users</th><th className="num">Gross in</th><th className="num">Cashback out</th><th className="num">Share</th></tr></thead>
            <tbody>
              {monthly.map((m) => (
                <tr key={m.month}><td>{m.month}</td><td className="num">{num(m.topups)}</td><td className="num">{num(m.users)}</td>
                  <td className="num">{money(m.gross)}</td><td className="num">{money(m.cashback)}</td>
                  <td className="num">{m.gross ? `${((m.cashback / m.gross) * 100).toFixed(1)}%` : "—"}</td></tr>
              ))}
              {!monthly.length && <tr><td colSpan={6} className="ma-empty">{ins ? "No data" : "Loading…"}</td></tr>}
            </tbody>
          </table></div>
        </div>

        {campaigns.length > 0 && (
          <div className="ma-card">
            <div className="ma-card-h"><h3>Recent campaigns</h3></div>
            <div className="ma-tablewrap"><table className="ma-table">
              <thead><tr><th>Campaign</th><th>Status</th><th>Window</th><th className="num">Paid</th></tr></thead>
              <tbody>
                {campaigns.slice(0, 6).map((c) => (
                  <tr key={c.id} className="clickable" onClick={() => onOpen(c)}>
                    <td>{c.name}<div className="sub">{c.kind} · {c.visibility}</div></td>
                    <td><span className={`ma-pill ${statusPill(c.status)}`}>{c.status}</span></td>
                    <td className="sub">{ts(c.starts_at)} → {ts(c.ends_at)}</td>
                    <td className="num">{money(c.awarded_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        )}
      </div>
    </>
  );
};

export default OverviewTab;
